// A price band buckets products by shopper-visible (effective) price.
// `min`/`max` are inclusive-lower/exclusive-upper bounds in dollars; either may be `null`
// for no bound (e.g. "Under $10" has no min, "$50+" has no max).
export const PRICE_RANGE_BAND_LABEL_MAX_LENGTH = 12;

export type PriceRangeBand = {
  // Not `id`: Apollo normalizes any field named `id`/`_id` into a global cache entity. Band ids
  // are per-dispensary slugs and would collide across stores if cached that way.
  bandId: string;
  label: string;
  min: number | null;
  max: number | null;
};

// Shown when a retailer has not configured their own bands.
export const DEFAULT_PRICE_RANGE_BANDS: PriceRangeBand[] = [
  { bandId: 'under-10', label: 'Under $10', min: null, max: 10 },
  { bandId: '10-25', label: '$10 - $25', min: 10, max: 25 },
  { bandId: '25-50', label: '$25 - $50', min: 25, max: 50 },
  { bandId: '50-plus', label: '$50+', min: 50, max: null },
];

/**
 * True when `price` falls within `[band.min, band.max)` — inclusive lower bound, exclusive upper
 * bound, so a $25.00 product lands in "$25 - $50" rather than double-counting into "$10 - $25".
 * A missing bound is treated as unbounded in that direction.
 */
export function isPriceWithinBand(price: number, band: Pick<PriceRangeBand, 'max' | 'min'>): boolean {
  if (price == null || Number.isNaN(price)) {
    return false;
  }
  if (band.min != null && price < band.min) {
    return false;
  }
  if (band.max != null && price >= band.max) {
    return false;
  }
  return true;
}
